import type { Bedbank } from '@luxuryescapes/contract-svc-bedbank'
import request from 'api/requestUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

import { templates } from '@luxuryescapes/lib-uri-templates'
import config from 'constants/config'

export interface CreateReservationEnquiryProps {
  propertyId: string
  roomTypeId: string
  roomRateId: string
  token?: string
  bedGroupId?: string
  rateGroupId?: string | null
  region: string
  rooms: Array<App.Occupants>
  checkIn: string
  checkOut: string
  price: number
  memberInclusive: number | undefined
  propertyFees: number | undefined
  csBooking: boolean
  isFlightBundle: boolean
  subscriber?: App.OrderSubscriber
  source?: string
  discount?: number
}

/** Identifies a loyalty package upgrade discount. Keep this value aligned with the mobile app. */
export const LOYALTY_UPGRADE_DISCOUNT_REASON = 'loyalty_package_upgrade'

/** Bedbank accepts multiple discount types; customer portal currently sends at most one. */
export function buildEnquiryDiscount(discount: number | undefined): Array<Bedbank.Discount> | undefined {
  if (!discount) return
  return [{ reason: LOYALTY_UPGRADE_DISCOUNT_REASON, amount: discount }]
}

const enquiryMap = (data: App.EnquiryResponse): App.BedbankEnquiry => {
  const paymentFields = (data.rate?.paymentMethod === 'propertyCollect' && data.rate?.payAtPropertyTotals)
    ? {
        paymentMethod: 'propertyCollect' as const,
        payAtPropertyTotals: {
          exclusive: data.rate.payAtPropertyTotals.exclusive,
          inclusive: data.rate.payAtPropertyTotals.inclusive,
          taxesAndFees: data.rate.payAtPropertyTotals.taxesAndFees,
          propertyFees: data.rate.payAtPropertyTotals.propertyFees,
          currencyCode: data.rate.payAtPropertyTotals.currencyCode,
        },
      }
    : { paymentMethod: 'leCollect' as const }

  const returnData: App.BedbankEnquiry = {
    sessionId: data.sessionId,
    price: data.price,
    propertyId: data.propertyId,
    roomTypeId: data.roomTypeId,
    roomRateId: data.roomRateId,
    bedGroupId: data.bedGroupId,
    rateGroupId: data.rateGroupId,
    occupancy: data.occupancy,
    checkIn: data.checkIn,
    checkOut: data.checkOut,
    isFlightBundle: data.isFlightBundle,
    luxplusPrice: data.luxplusPrice,
    userId: data.userId,
    ...paymentFields,
    paymentScheduleInfo: (config.PAYMENT_SCHEDULE_LPP_ENABLED && data.paymentSchedule) ? {
      staticSchedule: {
        templateId: data.paymentSchedule.templateId,
        supplierPaymentNumberOfDays: data.paymentSchedule.supplierDueDays,
      },
    } : undefined,
  }

  if (data.rate) {
    returnData.occupancyPricing = data.rate.occupancyPricing.map(op => {
      const { fees: _fees, ...opWithoutFees } = op
      const propertyFees = op.propertyFees ?? 0
      return {
        ...opWithoutFees,
        occupancy: op.occupancy,
        exclusive: op.exclusive,
        inclusive: op.inclusive,
        taxesAndFees: op.taxesAndFees,
        salesTax: op.salesTax,
        propertyFees,
        memberExclusive: op.luxPlusExclusive ?? 0,
        memberInclusive: op.luxPlusInclusive ?? 0,
        total: op.inclusive + propertyFees,
        memberTotal: op.luxPlusInclusive ? op.luxPlusInclusive + propertyFees : undefined,
      }
    })
    returnData.totals = {
      exclusive: data.rate.totals.exclusive,
      inclusive: data.rate.totals.inclusive,
      propertyFees: data.rate.totals.propertyFees,
      taxesAndFees: data.rate.totals.taxesAndFees,
      memberExclusive: data.rate.totals.luxPlusExclusive ?? 0,
      memberInclusive: data.rate.totals.luxPlusInclusive ?? 0,
      total: data.rate.totals.inclusive + (data.rate.totals.propertyFees ?? 0),
      memberTotal: data.rate.totals.luxPlusInclusive ? data.rate.totals.luxPlusInclusive + (data.rate.totals.propertyFees ?? 0) : undefined,
    }
  }

  if (data.customerRefundPolicy?.cancellationPolicies) {
    returnData.cancellationPolicies = data.customerRefundPolicy?.cancellationPolicies
  }

  return returnData
}

export function enquiry(payload: CreateReservationEnquiryProps) {
  return request.post<
    App.ApiResponse<Bedbank.CreateReservationEnquiryResponse>,
    Omit<Bedbank.CreateReservationEnquiryRequest, 'brand'>
  >(templates.bedbank.bedbankEnquiry.expand(), {
    propertyId: payload.propertyId,
    roomTypeId: payload.roomTypeId,
    roomRateId: payload.roomRateId,
    token: payload.token,
    bedGroupId: payload.bedGroupId,
    rateGroupId: payload.rateGroupId ?? undefined,
    isFlightBundle: payload.isFlightBundle,
    region: payload.region,
    occupancy: payload.rooms.map(generateOccupancyStringByRoom),
    checkIn: payload.checkIn,
    checkOut: payload.checkOut,
    price: payload.price,
    luxPlusPrice: payload.memberInclusive,
    propertyFees: payload.propertyFees,
    csBooking: payload.csBooking,
    subscriber: payload.subscriber,
    source: payload.source,
    discount: buildEnquiryDiscount(payload.discount),
  }, { credentials: 'include' })
    .then(data => enquiryMap(data.result))
}

interface CalendarProps {
  propertyId: string
  region: string
  nights: number
  roomId?: string
}

export function getCalendar(payload: CalendarProps) {
  return request.get<App.ApiResponse<Bedbank.CalendarResponse>>(templates.bedbank.bedbankCalendar.expand(payload), { credentials: 'include' })
    .then(data => data.result)
}

export function getEnquiry(sessionId: string) {
  return request.get<
    App.ApiResponse<Bedbank.CreateReservationEnquiryResponse>
  >(templates.bedbank.bedbankGetEnquiry.expand({ sessionId }), { credentials: 'include' })
    .then(data => enquiryMap(data.result))
}
